import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography, Paper, Stack, Alert, Snackbar, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsappSessionsService from '../services/whatsappSessions';
import { useWhatsappSessions } from './base/WhatsappSessionsProvider';
// QRCodeCanvas se carga dinámicamente para evitar romper el build si falta la dependencia
// (por ejemplo después de un npm install fallido). Si no está disponible mostramos fallback.
let StaticQRCodeCanvas = null;
try {
  // eslint-disable-next-line global-require
  const mod = require('qrcode.react');
  StaticQRCodeCanvas = mod.QRCodeCanvas || mod.default || null;
} catch (e) {
  StaticQRCodeCanvas = null;
}

/**
 * Componente que verifica la sesión de WhatsApp antes de renderizar children.
 * Flujo:
 *  1. Llama a /whatsapp/status.
 *  2. Si status === 'connected' -> renderiza children.
 *  3. Si status === 'disconnected' (o no conectado) -> solicita QR a /whatsapp/qr y lo muestra.
 *  4. Permite reintentar y hace polling hasta que se conecte.
 *
 * Props:
 *  - children: contenido protegido.
 *  - pollInterval (ms): intervalo de poll para re-chequear estado cuando se muestra QR.
 */
// Props:
//  - sessionId (opcional): si se pasa, el guard trabaja contra endpoints multi-sesión.
//    Si no se pasa, usa el servicio legacy single-session.
const WhatsappSessionGuard = ({ children, pollInterval = 5000, enabled = false, /* sessionId deprecated */ sessionId = null, autoCreateSession = false, onSessionCreated }) => {
  // (Se reubica después de isPublications) Auto-crear sesión si se habilita y no hay sessionId
  // Sólo activar lógica si estamos en /publicaciones (gating por pathname)
  const location = useLocation();
  // Aceptar variantes como /publicaciones, /publicaciones/, /publicaciones/lo-que-sea
  const isPublications = /\/publicaciones(\/|$)/.test(location?.pathname || '');
  // Auto-crear sesión si se habilita y no hay sessionId (multi-sesión)
  // Obtener contexto (si el provider no existe esto lanzará; asumimos que sí está en App). 
  // Si hubiera necesidad de modo sin provider, se debería envolver este guard fuera.
  const ctx = useWhatsappSessions();
  const { sessionId: contextSessionId, initializing: initializingProvider, createSession: createSessionCtx } = ctx || {};
  // Fallback local si el contexto aún no hidrata.
  const [localSessionId, setLocalSessionId] = useState(() => {
    try { return localStorage.getItem('whatsappSessionId') || null; } catch { return null; }
  });
  // effectiveSessionId: preferimos contexto, si no, localSessionId.
  const effectiveSessionId = contextSessionId || localSessionId;
  // Sync: si el contexto hidrata después y es distinto, actualizamos local
  useEffect(() => {
    if (contextSessionId && contextSessionId !== localSessionId) {
      setLocalSessionId(contextSessionId);
    }
  }, [contextSessionId, localSessionId]);
  // Safeguard: al refrescar, puede que sessionId tarde un tick en hidratarse desde localStorage/Provider.
  // Retrasamos la auto-creación unos milisegundos para evitar crear una sesión nueva si la anterior aún existe.
  useEffect(() => {
    let timer;
    const attempt = async () => {
      if (!activeRef.current) return;
      if (!autoCreateSession) return;
  if (effectiveSessionId) return; // ya existe
      try {
        const created = await (createSessionCtx ? createSessionCtx() : WhatsappSessionsService.createSession());
        const newId = created?.sessionId || created?.id;
        if (newId) {
          console.log('[WhatsappSessionGuard] Sesión auto-creada', newId);
          onSessionCreated?.(newId);
          // Persistir manualmente si el provider todavía no lo hizo
          try { localStorage.setItem('whatsappSessionId', newId); } catch {}
          setLocalSessionId(prev => prev || newId);
        }
      } catch (e) {
        console.warn('[WhatsappSessionGuard] Error creando sesión automática', e?.message);
      }
    };
    if (enabled && isPublications) {
      // Pequeño delay mínimo (100ms) sólo para dar tiempo al localStorage y re-render
      timer = setTimeout(attempt, 100);
    }
    return () => { if (timer) clearTimeout(timer); };
  // createSessionCtx y selectSessionCtx son estables del provider; se añaden para exhaustiveness
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSessionId, autoCreateSession, enabled, isPublications, onSessionCreated, createSessionCtx]);
  // Refs para control de ciclo de vida y activación real
  const mountedRef = useRef(true);
  const activeRef = useRef(false); // será true sólo si enabled && isPublications
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  useEffect(() => {
    const active = enabled && isPublications;
    activeRef.current = active;
    if (!active) {
      if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [enabled, isPublications]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null); // 'connected' | 'disconnected' | 'initializing' | 'error'
  // Detectar caso sin sesión y sin auto-creación para liberar loading y mostrar UI de ayuda
  // Grace period: damos tiempo a que el provider hidrate sessionId antes de declarar 'no-session'.
  useEffect(() => {
    if (!enabled || !isPublications) return;
    if (initializingProvider) return; // esperar a provider
    if (!effectiveSessionId) {
      if (autoCreateSession) {
        console.log('[WhatsappSessionGuard] No sessionId, creando...');
        (async () => {
          try {
            const created = await (createSessionCtx ? createSessionCtx() : WhatsappSessionsService.createSession());
            const newId = created?.sessionId || created?.id;
            if (newId) {
              onSessionCreated?.(newId);
              // Tras crear, disparar status inmediatamente
              console.log('[WhatsappSessionGuard] Sesión creada, consultando status inmediato');
              setLoading(true);
              try { localStorage.setItem('whatsappSessionId', newId); } catch {}
              setLocalSessionId(prev => prev || newId);
              checkStatus(newId);
            } else {
              setStatus('no-session');
              setLoading(false);
            }
          } catch (e) {
            console.warn('[WhatsappSessionGuard] Error creando sesión automática', e?.message);
            setStatus('error');
            setLoading(false);
          }
        })();
      } else {
        setStatus('no-session');
        setLoading(false);
      }
    } else {
      console.log('[WhatsappSessionGuard] sessionId presente (ctx/local), consultando status', effectiveSessionId);
      setLoading(true);
      checkStatus(effectiveSessionId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isPublications, effectiveSessionId, autoCreateSession, initializingProvider]);

  const [rawStatus, setRawStatus] = useState(null); // objeto completo devuelto por backend
  const [displayedQr, setDisplayedQr] = useState(null); // QR actualmente visible
  const [fetchedQr, setFetchedQr] = useState(null);     // Último QR obtenido (pendiente de mostrar si cambia)
  const lastHashRef = useRef(null);                    // Hash simple del último QR mostrado
  const lastDisplayedAtRef = useRef(0);                // timestamp cuando se mostró el QR actual
  const MIN_QR_REFRESH_MS = 12000; // mínimo 12s entre reemplazos de QR para evitar flicker constante
  const [error, setError] = useState(null);
  const [fetchingQR, setFetchingQR] = useState(false);
  const [expired, setExpired] = useState(false);
  const [showSnack, setShowSnack] = useState(false);
  const [restartLoading, setRestartLoading] = useState(false);
  const [QRCodeCanvas, setQRCodeCanvas] = useState(() => StaticQRCodeCanvas);
  const prevConnectedRef = useRef(false);
  const firstConnectedAtRef = useRef(null); // marca cuando se detectó conexión para estimar reauth
  const [nowTick, setNowTick] = useState(Date.now()); // ticker local para actualizar countdown sin esperar poll
  const pollTimerRef = useRef(null); // timeout scheduler en vez de setInterval
  const inFlightRef = useRef(false); // evita superposición de requests
  // Refs para countdown suave (local) de willCloseInMs
  const willCloseInitialRef = useRef(null); // valor willCloseInMs recibido del backend en el último fetch
  const willCloseBaseTsRef = useRef(null); // timestamp(Date.now()) cuando se capturó el valor anterior

  // Intento de carga dinámica si no existía en el require estático
  useEffect(() => {
    if (!activeRef.current) return; // evitar carga dinámica fuera de publicaciones
    if (!QRCodeCanvas) {
      import('qrcode.react')
        .then(m => setQRCodeCanvas(m.QRCodeCanvas || m.default || null))
        .catch(() => setQRCodeCanvas(null));
    }
  }, [QRCodeCanvas, isPublications]);

  // Función para generar hash simple (no criptográfica) para comparar strings base64 distintas
  const hashStr = (s) => {
    if (!s) return null;
    let h = 0; for (let i = 0; i < s.length; i += Math.max(1, Math.floor(s.length / 257))) { h = (h * 31 + s.charCodeAt(i)) >>> 0; }
    return h.toString(16);
  };

  // Util central para mapear state backend -> status interno UI
  const mapBackendState = useCallback((state, resp) => {
    if (state === 'open' || resp?.status?.connected === true) return 'connected';
    if (state === 'invalid') return 'invalid';
    if (state === 'qr') return 'waiting-qr';
    if (state === 'connecting') return 'initializing';
    return 'disconnected';
  }, []);

  const fetchQR = useCallback(async ({ forceFresh = false, forceDisplaySwap = false } = {}) => {
    if (!activeRef.current || !mountedRef.current) return;
    if (!effectiveSessionId) return; // no hay sesión todavía
    try {
      setFetchingQR(true);
      setError(null);
      const resp = await WhatsappSessionsService.getCachedQR(effectiveSessionId, { fresh: forceFresh, force: forceFresh });
      const incoming = resp?.qr || null;
      if (incoming) {
        // Si tenemos ya un QR mostrado y no se pidió swap forzado, respetar umbral de tiempo para evitar flicker
        if (!forceDisplaySwap && displayedQr) {
          const now = Date.now();
          const age = now - lastDisplayedAtRef.current;
          if (age < MIN_QR_REFRESH_MS) {
            // Guardamos en fetchedQr para aplicar luego
            setFetchedQr(incoming);
            setFetchingQR(false);
            return;
          }
        }
        setFetchedQr(incoming);
      } else if (!displayedQr) {
        setFetchedQr(null);
      }
    } catch (e) {
      setError(e?.message || 'Error obteniendo QR');
    } finally {
      setFetchingQR(false);
    }
  }, [displayedQr, effectiveSessionId]);

  // Helper: verificar si la sesión existe realmente. Si backend devuelve 404 se marca 'no-session'.
  const ensureSessionExists = useCallback(async (sidOverride) => {
    const sid = sidOverride || effectiveSessionId;
    if (!sid) return false;
    try {
      await WhatsappSessionsService.getStatus(sid);
      return true;
    } catch (e) {
      // Si es 404 (session no existe en backend porque se reinició el server), reset local
      const statusCode = e?.response?.status || e?.status || e?.code;
      if (statusCode === 404) {
        setStatus('no-session');
        setLoading(false);
        // Limpiamos marcas locales de QR
        setDisplayedQr(null);
        setFetchedQr(null);
        lastHashRef.current = null;
        return false;
      }
      // Otros errores: dejamos que checkStatus los maneje
      return true; // permitir que checkStatus intente
    }
  }, [effectiveSessionId]);

  const checkStatus = useCallback(async (sidOverride) => {
    if (!activeRef.current || !mountedRef.current) return;
    const sid = sidOverride || effectiveSessionId;
    if (!sid) return; // esperar auto-creación
    // Validar existencia antes de avanzar: si no existe, salimos.
    const exists = await ensureSessionExists(sid);
    if (!exists) return;
    try {
      setError(null);
      const resp = await WhatsappSessionsService.getStatus(sid);
      console.log('[WhatsappSessionGuard] Status response', resp?.status);
  // Backend actual: status: { state: 'connecting' | 'qr' | 'open', qrAvailable, reconnects }
  const state = resp?.status?.state;
  // qrAvailable ya no se usa directamente; usamos needsQr y qrExpired
  const needsQr = resp?.status?.needsQr === true;
  const qrExpired = resp?.status?.qrExpired === true;
  const qrMaxAgeMs = resp?.status?.qrMaxAgeMs;
  const lastErrorCode = resp?.status?.lastErrorCode;
  // Normalizar 'connected' a partir de state === 'open'
  const connected = state === 'open' || resp?.status?.connected === true;
      const prevRaw = rawStatus;
      const prevJid = prevRaw?.user?.jid;
      const newJid = resp?.status?.user?.jid;
      setRawStatus(resp?.status || null);
      // Mapear estados intermedios para UI si no está conectado
      setStatus(mapBackendState(state, resp));
      // Actualizar base local para countdown de cierre sólo si no existe aún o si el backend reporta un cambio real.
      // Caso: backend entrega SIEMPRE el valor máximo (ventana completa) -> no queremos reiniciar el countdown.
      // Caso: backend entrega valor decreciente -> sincronizamos cuando detectamos decremento.
      // Caso: backend extiende/renueva sesión (valor mucho mayor) -> reiniciamos.
      if (resp?.status?.willCloseInMs != null) {
        const incoming = resp.status.willCloseInMs;
        const prevInit = willCloseInitialRef.current;
        const nowTs = Date.now();
        if (prevInit == null) {
          // Primera vez: establecer baseline
            willCloseInitialRef.current = incoming;
            willCloseBaseTsRef.current = nowTs;
        } else {
          const decreased = incoming < prevInit - 1000; // disminuyó al menos 1s -> servidor da remaining real
          const extended = incoming > prevInit + 10000; // aumentó claramente (>10s) -> nueva ventana o extensión
          if (decreased || extended) {
            willCloseInitialRef.current = incoming;
            willCloseBaseTsRef.current = nowTs;
          }
          // Si incoming == prevInit (o dentro de +-1s) asumimos valor máximo constante -> mantenemos baseline original.
        }
      } else {
        willCloseInitialRef.current = null;
        willCloseBaseTsRef.current = null;
      }
      // Detectar desconexión en medio de la sesión
      if (prevConnectedRef.current && !connected) {
        setShowSnack(true);
      }
      prevConnectedRef.current = connected;
      if (connected) {
        // Limpieza inmediata de QR al entrar en estado open
        setDisplayedQr(null);
        setFetchedQr(null);
        setExpired(false);
        // Reiniciar base de reauth si:
        //  - no había base previa (primer connect) o
        //  - cambió el JID (nueva sesión escaneada) o
        //  - lastForcedReauthAt es null (ventana se reinicia en cada conexión) o
        //  - veníamos de un estado 'disconnected'
        const lastForcedReauthAt = resp?.status?.lastForcedReauthAt;
        const isNewSession = (!firstConnectedAtRef.current) || (prevJid && newJid && prevJid !== newJid);
        const cameFromDisconnected = !prevRaw?.connected && connected;
        if (isNewSession || cameFromDisconnected || lastForcedReauthAt == null) {
          firstConnectedAtRef.current = Date.now();
        }
      } else if (!connected) {
        if (state === 'invalid' || lastErrorCode === 401) {
          // Sesión inválida: no intentamos QR, requerimos reset o recreación
          setStatus('invalid');
        } else if (state === 'qr' || needsQr) {
          // Si el QR está expirado o cerca de expirar ( > 80% de qrMaxAge ) forzar fresh
          let forceFresh = false;
          if (qrExpired) forceFresh = true;
          else if (qrMaxAgeMs && resp?.status?.qrAgeMs != null) {
            const age = resp.status.qrAgeMs;
            if (age > qrMaxAgeMs * 0.8) forceFresh = true; // renovamos antes del vencimiento para evitar que el usuario escanee uno casi vencido
          }
          await fetchQR({ forceFresh });
        } else if (state === 'connecting') {
          // Intento proactivo por si el backend no actualizó state todavía pero ya generó QR
          await fetchQR({ forceFresh: true });
        } else {
          if (!displayedQr) setFetchedQr(null);
        }
      }
    } catch (e) {
      const statusCode = e?.response?.status || e?.status || e?.code;
      if (statusCode === 404) {
        // Sesión no existe en backend -> estado especial
        setStatus('no-session');
      } else {
        setError(e?.message || 'Error verificando estado');
        setStatus('error');
      }
    } finally {
      setLoading(false);
    }
  }, [fetchQR, rawStatus, displayedQr, effectiveSessionId, ensureSessionExists, mapBackendState]);

  // Reactive restore: si estaba en 'no-session' y aparece effectiveSessionId luego del grace period, lanzar checkStatus.
  // Nuevo efecto: cuando aparece sessionId por primera vez o cambia, forzar status inmediato si no estamos cargando
  const lastSessionIdRef = useRef(effectiveSessionId);
  useEffect(() => {
    if (!enabled || !isPublications) return;
    if (!effectiveSessionId) return;
    const prev = lastSessionIdRef.current;
    if (prev !== effectiveSessionId || status == null) {
      lastSessionIdRef.current = effectiveSessionId;
      console.log('[WhatsappSessionGuard] sessionId change/appearance -> checkStatus inmediato', effectiveSessionId);
      setLoading(true);
      checkStatus(effectiveSessionId);
    }
  }, [effectiveSessionId, enabled, isPublications, status, checkStatus]);

  // Determinar próximo intervalo dinámicamente (ms)
  const computeNextInterval = useCallback(() => {
    if (!activeRef.current) return Number.MAX_SAFE_INTEGER;
    if (status === 'connected') return Math.max(pollInterval * 2, 8000);
    const qrAvailable = rawStatus?.qrAvailable;
    return qrAvailable ? Math.max(2000, Math.floor(pollInterval * 0.6)) : Math.max(1500, Math.floor(pollInterval * 0.4));
  }, [status, pollInterval, rawStatus]);

  const runPoll = useCallback(async () => {
    if (!activeRef.current || !mountedRef.current) return;
    if (inFlightRef.current) return; // protección extra
    inFlightRef.current = true;
    await checkStatus();
    inFlightRef.current = false;
    // Programar próximo ciclo sólo si el componente sigue montado
    const next = computeNextInterval();
    pollTimerRef.current = setTimeout(runPoll, next);
  }, [checkStatus, computeNextInterval]);

  // Efecto para decidir cuándo mostrar fetchedQr (si cambió realmente)
  useEffect(() => {
    if (!activeRef.current) return;
    if (!fetchedQr) return; // nada que mostrar
    const newHash = hashStr(fetchedQr);
    if (lastHashRef.current === newHash) return; // mismo contenido semántico
    // Preload si es imagen base64 para asegurar que el nodo esté listo antes del swap
    const doSwap = () => {
      lastHashRef.current = newHash;
      setDisplayedQr(fetchedQr);
      lastDisplayedAtRef.current = Date.now();
    };
    if (fetchedQr.startsWith('data:image')) {
      const img = new Image();
      img.onload = () => { doSwap(); };
      img.onerror = () => { doSwap(); };
      img.src = fetchedQr;
    } else {
      doSwap();
    }
  }, [fetchedQr]);

  // Scheduler único: arranca una vez cuando termina el primer loading inicial
  useEffect(() => {
    if (!activeRef.current) return undefined; // no scheduler fuera de publicaciones
    if (!loading && pollTimerRef.current == null) {
      pollTimerRef.current = setTimeout(runPoll, computeNextInterval());
    }
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    };
  }, [loading, runPoll, computeNextInterval, isPublications]);

  // Primer chequeo inmediato al montar (antes de iniciar loop)
  useEffect(() => {
    if (!activeRef.current) return; // gate primer chequeo
    // Evitar llamada a status si aún no hay sessionId y autoCreateSession desactivado.
    if (!effectiveSessionId && !autoCreateSession) {
      // Dejamos que el efecto de grace decida luego; no marcamos inmediatamente no-session
      return;
    }
    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublications]);

  // Ticker de 1s para countdown y métricas en vivo
  useEffect(() => {
    if (!activeRef.current) return undefined; // no ticker si no estamos en publicaciones
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isPublications]);

  // Marcar expiración cuando willCloseInMs llega a 0
  useEffect(() => {
    if (!activeRef.current) return; // gate expiración
    if (status === 'connected' && willCloseInitialRef.current != null && willCloseBaseTsRef.current != null) {
      const remaining = willCloseInitialRef.current - (Date.now() - willCloseBaseTsRef.current);
      if (remaining <= 0 && !expired) {
        setExpired(true);
        setShowSnack(true);
      }
    }
  }, [status, expired, isPublications]);

  // Si no estamos en /publicaciones, renderizar children directamente sin lógica ni UI extra
  if (!enabled || !isPublications) {
    // Evitar side-effects: asegurar loading false y timers limpios
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    return <>{children}</>;
  }

  // Si el provider sigue inicializando mostramos spinner genérico
  if (initializingProvider || loading) {
    return (
      <Box p={4} display='flex' flexDirection='column' alignItems='center' justifyContent='center' gap={2}>
        <CircularProgress />
        <Typography variant='body2'>Verificando sesión de WhatsApp...</Typography>
      </Box>
    );
  }

  // Estado especial: no hay sesión y no se auto-crea
  if (status === 'no-session') {
    return (
      <Box p={4} display='flex' flexDirection='column' alignItems='center' gap={2}>
        <Typography variant='h6'>Sin sesión de WhatsApp</Typography>
        <Typography variant='body2' align='center'>Creá una sesión para poder enviar publicaciones y escanear un QR.</Typography>
        <Button
          variant='contained'
          onClick={async () => {
            try {
              setLoading(true);
              const created = await WhatsappSessionsService.createSession();
              const newId = created?.sessionId || created?.id;
              if (newId) {
                onSessionCreated?.(newId);
                console.log('[WhatsappSessionGuard] Sesión creada manualmente, status inmediato');
                setLoading(true);
                try { localStorage.setItem('whatsappSessionId', newId); } catch {}
                setLocalSessionId(prev => prev || newId);
                checkStatus(newId);
              } else {
                setLoading(false);
              }
            } catch (e) {
              setError(e?.message || 'Error creando sesión');
              setLoading(false);
            }
          }}
        >Crear sesión</Button>
        <Button variant='outlined' onClick={() => checkStatus()} disabled={loading}>Reintentar</Button>
      </Box>
    );
  }

  if (status !== 'connected') {
    const lastActivityDate = rawStatus?.lastActivity ? new Date(rawStatus.lastActivity) : null;
    const idleMs = rawStatus?.idleMs;
    const now = nowTick;
    // Preferimos countdown local derivado si disponemos de base; fallback al valor crudo.
    const rawWillCloseInMs = rawStatus?.willCloseInMs;
    const localWillCloseInMs = (willCloseInitialRef.current != null && willCloseBaseTsRef.current != null)
      ? Math.max(0, willCloseInitialRef.current - (now - willCloseBaseTsRef.current))
      : rawWillCloseInMs;
    const reconnectAttempts = rawStatus?.reconnectAttempts;
    const qrAvailable = rawStatus?.qrAvailable;
    const sinceActivitySec = lastActivityDate ? Math.floor((now - lastActivityDate.getTime()) / 1000) : null;
    const idleSec = idleMs != null ? Math.floor(idleMs / 1000) : null;
    const closeInSec = localWillCloseInMs != null ? Math.floor(localWillCloseInMs / 1000) : null;

    return (
      <Box p={3} display='flex' flexDirection='column' alignItems='center' gap={2}>
        <Typography variant='h6'>WhatsApp {status === 'initializing' ? 'inicializando' : (status === 'waiting-qr' ? 'esperando QR' : 'no conectado')}</Typography>
        {error && <Typography variant='body2' color='error'>{error}</Typography>}
        <Typography variant='body2' align='center'>
          {status === 'initializing' && 'Preparando socket y credenciales...'}
          {status === 'waiting-qr' && 'Escaneá el código QR con tu app de WhatsApp para iniciar sesión.'}
          {status === 'disconnected' && 'Escaneá un QR (reinicia la sesión si hace falta) para conectar.'}
        </Typography>
        <Paper elevation={3} sx={{ p: 2, minWidth: 260, minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box position='relative' width={240} height={240} display='flex' alignItems='center' justifyContent='center'>
            {displayedQr && (
              displayedQr.startsWith('data:image') ? (
                <img src={displayedQr} alt='QR' style={{ width: 240, height: 240 }} />
              ) : QRCodeCanvas ? (
                <QRCodeCanvas value={displayedQr} size={240} includeMargin level='M' />
              ) : (
                <Box display='flex' flexDirection='column' alignItems='center' gap={1}>
                  <Typography variant='caption' sx={{ maxWidth: 240, wordBreak: 'break-all' }}>QR token: {displayedQr}</Typography>
                  <Tooltip title='Copiar token'>
                    <IconButton size='small' onClick={() => navigator?.clipboard?.writeText(displayedQr)}>
                      <ContentCopyIcon fontSize='inherit' />
                    </IconButton>
                  </Tooltip>
                </Box>
              )
            )}
            {!displayedQr && !fetchingQR && (
              <Typography variant='caption'>
                {rawStatus?.state === 'connecting' && 'Esperando QR...'}
                {rawStatus?.state === 'qr' && 'Cargando QR...'}
                {rawStatus?.state !== 'connecting' && rawStatus?.state !== 'qr' && !rawStatus?.qrAvailable && 'QR no disponible'}
              </Typography>
            )}
            {fetchingQR && (
              <Box position='absolute' top={0} left={0} right={0} bottom={0} display='flex' alignItems='center' justifyContent='center' bgcolor={displayedQr ? 'rgba(255,255,255,0.55)' : 'transparent'}>
                <CircularProgress size={displayedQr ? 56 : 36} />
              </Box>
            )}
          </Box>
        </Paper>
        <Stack spacing={0.5} sx={{ minWidth: 280 }}>
          {sinceActivitySec != null && <Typography variant='caption'>Última actividad hace {sinceActivitySec}s</Typography>}
          {idleSec != null && <Typography variant='caption'>Inactivo: {idleSec}s</Typography>}
            {closeInSec != null && <Typography variant='caption'>Se cerrará en ~{closeInSec}s</Typography>}
          {(() => {
            const reconnects = rawStatus?.reconnects != null ? rawStatus.reconnects : reconnectAttempts;
            return reconnects != null ? <Typography variant='caption'>Reintentos: {reconnects}</Typography> : null;
          })()}
        </Stack>
        <Box display='flex' gap={1} flexWrap='wrap' justifyContent='center'>
          <Button variant='contained' size='small' onClick={checkStatus} disabled={fetchingQR}>Reintentar estado</Button>
          <Button variant='outlined' size='small' color='secondary' onClick={() => fetchQR({ forceFresh: true, forceDisplaySwap: true })} disabled={fetchingQR || !qrAvailable}>Forzar nuevo QR{effectiveSessionId ? ` (${effectiveSessionId.substring(0,6)}...)` : ''}</Button>
          <Button
            variant='outlined'
            color='warning'
            size='small'
            disabled={restartLoading}
            onClick={async () => {
              try {
                setRestartLoading(true);
                setError(null);
                if (effectiveSessionId) {
                  await WhatsappSessionsService.resetSession(effectiveSessionId);
                  WhatsappSessionsService.clearQRCache(effectiveSessionId);
                } else {
                    // Removed legacy restart code
                    // await WhatsappService.restart();
                    // await WhatsappService.clearQRCache?.();
                }
                // Reiniciamos marcadores locales
                firstConnectedAtRef.current = null;
                setDisplayedQr(null);
                setExpired(false);
                // Damos un pequeño respiro al backend para regenerar QR
                setTimeout(() => { checkStatus(); }, 1200);
              } catch (e) {
                setError(e?.message || 'Error reiniciando sesión');
              } finally {
                setRestartLoading(false);
              }
            }}
          >
            {restartLoading ? 'Reiniciando...' : 'Reiniciar sesión'}
          </Button>
        </Box>
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {lastDisplayedAtRef.current > 0 && displayedQr && (
            <Typography variant='caption'>QR mostrado hace {Math.floor((Date.now() - lastDisplayedAtRef.current)/1000)}s</Typography>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <>
      <Snackbar open={showSnack} autoHideDuration={4000} onClose={() => setShowSnack(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setShowSnack(false)} severity={expired ? 'warning' : 'info'} sx={{ width: '100%' }}>
          {expired ? 'La sesión de WhatsApp ha expirado.' : 'La sesión de WhatsApp se ha desconectado.'}
        </Alert>
      </Snackbar>
      {/* Barra de estado cuando está conectado */}
      {status === 'connected' && (
        (() => {
          // Countdown local suave para expiración.
          const rawWillCloseInMs = rawStatus?.willCloseInMs;
          const willCloseInMs = (willCloseInitialRef.current != null && willCloseBaseTsRef.current != null)
            ? Math.max(0, willCloseInitialRef.current - (nowTick - willCloseBaseTsRef.current))
            : rawWillCloseInMs;
          const forceReauthMs = rawStatus?.forceReauthMs; // ventana total
          const nextForcedReauthAt = rawStatus?.nextForcedReauthAt; // timestamp absoluto si backend lo provee
          let reauthLeftSec = null;
          if (forceReauthMs != null) {
            if (nextForcedReauthAt) {
              // Cálculo directo desde timestamp absoluto
              const remaining = Math.max(0, Math.floor((nextForcedReauthAt - nowTick) / 1000));
              reauthLeftSec = remaining;
            } else {
              // Base: si no hay lastForcedReauthAt (null) asumimos inicio de ventana al primer connect
              const base = rawStatus?.lastForcedReauthAt || firstConnectedAtRef.current || Date.now();
              const elapsed = nowTick - base;
              const remaining = forceReauthMs - elapsed;
              reauthLeftSec = remaining > 0 ? Math.floor(remaining / 1000) : 0;
            }
          }
          const closeInSec = willCloseInMs != null ? Math.max(0, Math.floor(willCloseInMs / 1000)) : null;
          const highlight = (sec) => sec != null && sec <= 60; // resaltamos último minuto
          const formatHM = (sec) => {
            if (sec == null) return '';
            if (sec < 60) return `${sec}s`;
            const h = Math.floor(sec / 3600);
            const m = Math.floor((sec % 3600) / 60);
            const remSec = sec % 60;
            if (h > 0) {
              if (m > 0) return `${h}h ${m}m` + (sec < 3600 ? '' : '');
              return `${h}h`;
            }
            if (m > 0) {
              return remSec > 0 && m < 10 ? `${m}m ${remSec}s` : `${m}m`;
            }
            return `${sec}s`;
          };
          return (
            <Box mb={1} px={2} py={1} bgcolor={highlight(closeInSec) || highlight(reauthLeftSec) ? 'warning.light' : 'grey.100'} borderBottom='1px solid' borderColor='divider' display='flex' flexWrap='wrap' gap={2} alignItems='center'>
              {(() => {
                // Intentamos extraer identidad del dispositivo / sesión
                // Campos potenciales según distintas libs backend:
                //  - status.user: { id / wid / name / pushName / number }
                //  - status.me / status.account / status.client
                //  - wid con formato 1234567890@s.whatsapp.net
                const s = rawStatus || {};
                const candidateObj = s.user || s.me || s.account || s.client || null;
                if (!candidateObj) return null; // user todavía null si no se escaneó
                const jid = candidateObj.jid || candidateObj.id || candidateObj.wid || s.jid || s.wid || s.sessionId;
                const pushName = candidateObj.name || candidateObj.pushName || s.name || s.pushName;
                let phone = candidateObj.number || s.number;
                // Normalizar si viene sufijo tipo 5493814124209:50 -> quitar : y lo que sigue
                if (phone && phone.includes(':')) phone = phone.split(':')[0];
                // Si no tenemos number explícito, intentar derivarlo del JID
                if (!phone && typeof jid === 'string' && jid.includes('@')) {
                  phone = jid.split('@')[0];
                  if (phone.includes(':')) phone = phone.split(':')[0];
                }
                const display = pushName ? (phone ? `${pushName} (${phone})` : pushName) : (phone || null);
                return display ? (
                  <Typography variant='caption' sx={{ fontWeight: 500 }}>
                    {display}
                  </Typography>
                ) : null;
              })()}
              {closeInSec != null && (
                <Typography variant='caption' sx={{ fontWeight: highlight(closeInSec) ? 700 : 400 }}>
                  Sesión expira en: {formatHM(closeInSec)}
                </Typography>
              )}
              {reauthLeftSec != null && (
                <Typography variant='caption' sx={{ fontWeight: highlight(reauthLeftSec) ? 700 : 400 }}>
                  Reautenticación forzada en: {formatHM(reauthLeftSec)}
                </Typography>
              )}
              {(closeInSec == null && reauthLeftSec == null) && (
                <Typography variant='caption'>Sesión conectada</Typography>
              )}
            </Box>
          );
        })()
      )}
      {children}
    </>
  );
};

export default WhatsappSessionGuard;
